import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import {
  BoltIcon,
  BoltSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ServerIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import DisabledTextInput from "../forms/DisabledTextInput";
import { useSearchParams } from "react-router-dom";
import Filters from "../layout/Filters";
import Pagination from "../layout/Pagination";
import {
  forgetAppliance,
  getAppliances,
  getAppliancesConnectivity,
  getAppliancesPower,
  togglePowerOfAppliance,
} from "../../slices/smart_control";
import Indicator from "../layout/Indicator";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { XCircleIcon } from "@heroicons/react/24/outline";

export default function Appliances() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const pageNum =
    searchParams.get("page") === null
      ? 1
      : parseInt(searchParams.get("page") as string);
  const searchModel = searchParams.get("model");

  // Fetch data from server
  useEffect(() => {
    dispatch(getAppliances({ pageNum, model: searchModel }));
    dispatch(getAppliancesConnectivity({ pageNum, model: searchModel }));
    dispatch(getAppliancesPower({ pageNum, model: searchModel }));
  }, [dispatch, pageNum, searchModel]);

  // Set state
  const [model, setModel] = useState({ value: "" });
  const [selectedAppliance, setSelectedAppliance] = useState<{
    id: string;
    last_updated_by: string;
    datetime_created: string;
    datetime_updated: string | null;
    name: string;
    model: string;
    serial_number: string | null;
    associated_room: string | null;
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showForgetConfirmation, setShowForgetConfirmation] =
    useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { appliances, options } = useAppSelector((state) => state.smartControl);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectApplianceToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [applianceId] = args;
    const applianceData = appliancesData.filter(
      (app) => app.id === applianceId,
    )[0];
    setSelectedAppliance({
      id: applianceData.id,
      last_updated_by: applianceData.last_updated_by,
      datetime_created: applianceData.datetime_created,
      datetime_updated: applianceData.datetime_updated,
      name: applianceData.name,
      model: applianceData.model[1],
      serial_number: applianceData.serial_number,
      associated_room:
        applianceData.associated_room === null
          ? null
          : applianceData.associated_room[1],
    });
    setShowEditModal(true);
  }

  function buildConnectivityIndicator(applianceId: string) {
    let online = null;
    if (appliances.connectivity.data === null) {
      online = null;
    } else {
      const candidate = appliances.connectivity.data.filter(
        (app) => app.id === applianceId,
      )[0];
      if (candidate === undefined) {
        online = null;
      } else {
        online = candidate.online;
      }
    }
    if (online === null) {
      return (
        <>
          <BoltSlashIcon
            className="h-5 w-5 text-secondary mr-1"
            aria-hidden="true"
          />
          {translateText("unknown", language)}
        </>
      );
    } else if (online === true) {
      return (
        <>
          <BoltIcon className="h-5 w-5 text-success mr-1" aria-hidden="true" />
          {translateText("online", language)}
        </>
      );
    } else {
      return (
        <>
          <BoltSlashIcon
            className="h-5 w-5 text-danger mr-1"
            aria-hidden="true"
          />
          {translateText("offline", language)}
        </>
      );
    }
  }

  function buildPowerIndicator(applianceId: string) {
    let online = null;
    if (appliances.power.data === null) {
      online = null;
    } else {
      const candidate = appliances.power.data.filter(
        (app) => app.id === applianceId,
      )[0];
      if (candidate === undefined) {
        online = null;
      } else {
        online = candidate.on;
      }
    }
    if (online === null) {
      return "light";
    } else if (online === true) {
      return "success";
    } else {
      return "danger";
    }
  }

  function isPowerOn(applianceId: string) {
    let online = null;
    if (appliances.power.data === null) {
      online = null;
    } else {
      const candidate = appliances.power.data.filter(
        (app) => app.id === applianceId,
      )[0];
      if (candidate === undefined) {
        online = null;
      } else {
        online = candidate.on;
      }
    }
    if (online === true) return true;
    return false;
  }

  async function toggleAppliancePower(e: any, args: any[]) {
    e.preventDefault();
    const [toggle, applianceId] = args;
    const res: any = await dispatch(
      togglePowerOfAppliance({ applianceId, toggle }),
    );
    setShowEditModal(false);
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_appliance_power", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function forgetSelectedAppliance(e: any, args: any[]) {
    e.preventDefault();
    const [applianceId] = args;
    const res: any = await dispatch(forgetAppliance({ applianceId }));
    setShowEditModal(false);
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_appliance_forget", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function showForgetConfirmationMessage(e: any, args: any[]) {
    e.preventDefault();
    setShowForgetConfirmation(true);
  }

  function confirmForgetYes(e: any, args: any[]) {
    e.preventDefault();
    forgetSelectedAppliance(e, args);
  }

  function confirmForgetNo(e: any, args: any[]) {
    e.preventDefault();
    setShowForgetConfirmation(false);
  }

  // Format rooms data and filter by query param tag
  const appliancesData = appliances.data === null ? [] : appliances.data;
  const applianceModelOptions =
    options.model === undefined
      ? []
      : Object.keys(options.model).map((key) => ({
          value: key,
          label: options.model[key][language],
        }));

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("appliances_title", language)} />

      {/* Edit appliance modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_device", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={() => {}}
        showActionButtons={false}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {selectedAppliance?.name}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Created at */}
              <DisabledTextInput
                id="created_at"
                label={translateText("created_at", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedAppliance === null
                    ? ""
                    : selectedAppliance.datetime_created
                }
              />

              {/* Updated by */}
              {selectedAppliance !== null &&
                selectedAppliance.last_updated_by !== null && (
                  <DisabledTextInput
                    id="updated_by"
                    label={translateText("updated_by", language)}
                    className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                    showLabel={true}
                    value={
                      selectedAppliance === null
                        ? ""
                        : selectedAppliance.last_updated_by
                    }
                  />
                )}

              {/* Name */}
              <DisabledTextInput
                id="name"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={selectedAppliance === null ? "" : selectedAppliance.name}
              />

              {/* Model */}
              <DisabledTextInput
                id="model"
                label={translateText("model", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedAppliance === null ? "" : selectedAppliance.model
                }
              />

              {/* Serial number */}
              <DisabledTextInput
                id="serial_number"
                label={translateText("serial_number", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedAppliance === null
                    ? ""
                    : selectedAppliance.serial_number === null
                    ? ""
                    : selectedAppliance.serial_number
                }
              />

              {/* Name */}
              <DisabledTextInput
                id="associated_room"
                label={translateText("associated_room", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedAppliance === null
                    ? ""
                    : selectedAppliance.associated_room === null
                    ? ""
                    : selectedAppliance.associated_room
                }
              />

              {selectedAppliance !== null && (
                <>
                  {/* Buttons */}
                  <div className="grid sm:grid-cols-2 grid-cols-1 gap-2">
                    {/* Power off/on */}
                    {isPowerOn(selectedAppliance.id) ? (
                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "danger")}`}
                        onClick={toggleAppliancePower}
                        onClickArgs={[false, selectedAppliance.id]}
                      >
                        <BoltSlashIcon
                          className="w-5 h-5 mr-2"
                          aria-hidden="true"
                        />
                        {translateText("power_off", language)}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "success")}`}
                        onClick={toggleAppliancePower}
                        onClickArgs={[true, selectedAppliance.id]}
                      >
                        <BoltIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                        {translateText("power_on", language)}
                      </Button>
                    )}

                    {/* Forget appliance */}
                    <Button
                      type="button"
                      className={`${getButtonStyle("solid", "danger")}`}
                      onClick={showForgetConfirmationMessage}
                    >
                      <XMarkIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                      {translateText("forget_appliance", language)}
                    </Button>
                  </div>

                  {/* Forget appliance confirmation */}
                  {showForgetConfirmation && (
                    <div className="mt-2">
                      <p className="text-left text-sm text-black">
                        {translateText("confirm_forget_appliance", language)}
                      </p>
                      <div className="grid grid-cols-2 mt-2">
                        <Button
                          type="button"
                          className={`${getButtonStyle("solid", "hoverLight")}`}
                          onClick={confirmForgetNo}
                        >
                          <XCircleIcon
                            className={`h-5 w-5 text-secondary mr-1`}
                            aria-hidden="true"
                          />
                          <span className="text-secondary">
                            {translateText("delete_no", language)}
                          </span>
                        </Button>
                        <Button
                          type="button"
                          className={`${getButtonStyle(
                            "solid",
                            "hoverDanger",
                          )}`}
                          onClick={confirmForgetYes}
                          onClickArgs={[selectedAppliance.id]}
                        >
                          <CheckCircleIcon
                            className={`h-5 w-5 text-danger mr-1`}
                            aria-hidden="true"
                          />
                          <span className="text-danger">
                            {translateText("delete_yes", language)}
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {appliances.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "model",
                  name: "Model",
                  options: applianceModelOptions,
                },
              ]}
              queryParams={[["model", searchModel]]}
              handlers={{
                model: {
                  state: model,
                  stateName: "value",
                  onChange: setModel,
                },
              }}
              loading={appliances.loading}
              baseUrl="/smart-control/appliances"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {appliancesData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {appliancesData.map((app) => (
                    <a
                      href="#!"
                      key={app.id}
                      onClick={(e) => selectApplianceToEdit(e, [app.id])}
                      className="relative"
                    >
                      <Card
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {app.name}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <ServerIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {app.model[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            {buildConnectivityIndicator(app.id)}
                          </div>
                        </div>
                      </Card>

                      {/* Power indicator */}
                      <Indicator
                        size={3}
                        colour={buildPowerIndicator(app.id)}
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>

                <Pagination
                  className="mt-10"
                  showPrevious={appliances.pagination.has_previous}
                  showNext={appliances.pagination.has_next}
                  currentPage={appliances.pagination.current_page}
                  totalPages={appliances.pagination.total_pages}
                  totalObjects={appliances.pagination.total_objects}
                  queryParams={["model"]}
                />
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_appliances", language)}
                subtext={translateText("no_appliances_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
